import { CircleAlert, ErrorRounded } from 'components/icons';
import { FormattedDate } from 'components/misc/FormattedDate';
import { useIsNewDesign } from 'components/tables/list-table/filter-bar/useIsNewDesign';
import type { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import { t } from 'utils/getTranslations';
import { fieldName } from './DraftSaveMode';
import type { DraftActions } from './useDraftActions';

/**
 * Heads a draft's page: what a draft is, what is left to fill before it can be created, and when it is deleted.
 * It is there as the page opens, so it is a status rather than an alert. Nothing on a record that is no draft; Fill
 * them in is offered to who can edit it.
 */
export function DraftNotice({ actions }: { actions: DraftActions | null }) {
    const isNewDesign = useIsNewDesign();
    if (!actions?.draft) return null;
    const { draft } = actions;
    const { missingFields, deletesOn } = draft;
    const onFill = actions.canEdit ? actions.fill : undefined;
    return (
        <div
            role="status"
            className="border-warning-border bg-warning-light text-fg new:border-changed-border new:bg-changed new:text-changed-ink new:text-meta new:rounded-[10px] new:gap-x-4 flex flex-wrap items-center gap-x-3 gap-y-1.5 rounded-sm border px-4 py-2.5"
        >
            {isNewDesign ? null : <ErrorRounded className="text-warning-dark size-[18px] shrink-0" />}
            <span className="flex-[1_1_24rem] text-pretty">
                <FormattedMessage
                    id="draft.notice"
                    values={{
                        record: t(`create-dialog.record.${draft.type}`),
                        count: missingFields.length,
                        fields: missingFields.map(fieldName).join(', '),
                        b: (chunks: ReactNode) => (
                            <strong key="b" className="font-semibold">
                                {chunks}
                            </strong>
                        ),
                    }}
                />
                {deletesOn ? (
                    <>
                        {' '}
                        <FormattedMessage
                            id="draft.expires-on"
                            values={{ date: <FormattedDate value={deletesOn} /> }}
                        />
                        .
                    </>
                ) : null}
            </span>
            {onFill && missingFields.length > 0 ? (
                <button
                    type="button"
                    onClick={onFill}
                    className="text-link new:text-changed-ink cursor-pointer font-semibold hover:underline"
                >
                    {t('draft.notice.fill')}
                </button>
            ) : null}
        </div>
    );
}

/** Stands in for a required value a draft has not been given yet, where its page shows the value. */
export function DraftMissingValue() {
    const isNewDesign = useIsNewDesign();
    const Icon = isNewDesign ? CircleAlert : ErrorRounded;
    return (
        <span className="text-error-darker new:text-danger-ink inline-flex items-center gap-1.5">
            <Icon className="size-3.5 shrink-0" />
            {t('draft.missing-value')}
        </span>
    );
}
